import { router, usePage } from '@inertiajs/react'
import { useEffect, useRef, useState } from 'react'
import Icon from '../../../design-system/components/Icon'
import { useI18n } from '../../../lib/useI18n'

/**
 * Portal company switcher (docs/architecture.md §6.1, ADR-0015) — one portal user
 * may belong to several customers; this re-scopes every screen to the chosen one
 * by posting to /switch_company. Lives under the brand in the portal sidebar. A
 * single-company user just sees a static chip.
 */
type Company = { id: string; name: string; role?: string }

function initialsOf(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean)
  return (
    parts
      .slice(0, 2)
      .map((w) => w[0])
      .join('') || name.slice(0, 2)
  ).toUpperCase()
}

export default function CompanySwitcher() {
  const { t } = useI18n()
  const { props } = usePage()
  const companies = (props.companies as Company[] | undefined) ?? []
  const current = props.current_company as { id: string; name: string } | undefined
  const active = companies.find((c) => c.id === current?.id) ?? companies[0]
  const multi = companies.length > 1
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  if (!active) return null

  function switchTo(id: string) {
    setOpen(false)
    if (id === active.id) return
    router.post('/switch_company', { customer_id: id })
  }

  const chip = (
    <div className="flex items-center gap-2.5">
      <div className="grid h-8 w-8 flex-none place-items-center rounded-[9px] bg-accent text-[11px] font-bold text-accent-ink">
        {initialsOf(active.name)}
      </div>
      <div className="min-w-0 flex-1 text-left">
        <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-[var(--nav-faint)]">
          {multi
            ? t('portal.company_switcher.active_company')
            : t('portal.company_switcher.signed_in_as')}
        </div>
        <div className="truncate text-[13px] font-semibold text-white">{active.name}</div>
      </div>
      {multi ? (
        <Icon name="chevron-down" size={15} className="flex-none text-[var(--nav-faint)]" />
      ) : null}
    </div>
  )

  if (!multi) {
    return <div className="rounded-[10px] bg-[var(--nav-bg-2)] px-2.5 py-2">{chip}</div>
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="w-full rounded-[10px] bg-[var(--nav-bg-2)] px-2.5 py-2 hover:brightness-110"
      >
        {chip}
      </button>
      {open ? (
        <div
          role="menu"
          className="absolute right-0 left-0 z-50 mt-1 overflow-hidden rounded-xl border border-border bg-surface py-1 shadow-[var(--shadow)]"
        >
          <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-ink-muted">
            {t('portal.company_switcher.your_companies', { count: companies.length })}
          </div>
          {companies.map((c) => {
            const on = c.id === active.id
            return (
              <button
                key={c.id}
                type="button"
                role="menuitem"
                onClick={() => switchTo(c.id)}
                className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm hover:bg-surface-2"
              >
                <span className="grid h-7 w-7 flex-none place-items-center rounded-md bg-surface-3 text-[11px] font-bold text-ink-2">
                  {initialsOf(c.name)}
                </span>
                <span className="min-w-0 flex-1">
                  <span className={`block truncate text-ink ${on ? 'font-semibold' : ''}`}>
                    {c.name}
                  </span>
                  {c.role ? (
                    <span className="block truncate text-xs text-ink-muted">
                      {t(`portal.team.role.${c.role}`)}
                    </span>
                  ) : null}
                </span>
                {on ? <Icon name="check" size={15} className="flex-none text-accent" /> : null}
              </button>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}
